import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { SortableHead, useSortedRows, toggleSort, type SortState } from "@/components/admin/SortableHead";

type TeamSortKey = "full_name" | "email" | "role";

interface Row { user_id: string; role: string; email: string | null; full_name: string | null; }

export default function AdminTeam() {
  const [rows, setRows] = useState<Row[]>([]);
  const [me, setMe] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState<TeamSortKey> | null>({ key: "full_name", dir: "asc" });
  const sortedRows = useSortedRows(rows, sort, (r, k) => (r as any)[k]);

  const load = async () => {
    const { data: roles, error } = await supabase.from("user_roles").select("user_id, role");
    if (error) { toast.error(error.message); return; }
    const ids = Array.from(new Set((roles ?? []).map(r => r.user_id)));
    const { data: profiles } = await supabase.from("profiles").select("id, email, full_name").in("id", ids.length ? ids : ["00000000-0000-0000-0000-000000000000"]);
    const map = new Map(profiles?.map(p => [p.id, p]) ?? []);
    setRows((roles ?? []).map(r => ({ user_id: r.user_id, role: r.role, email: map.get(r.user_id)?.email ?? null, full_name: map.get(r.user_id)?.full_name ?? null })));
  };

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setMe(data.user?.id ?? null));
    load();
  }, []);

  const removeAdmin = async (userId: string) => {
    if (!confirm("Remove admin access for this user?")) return;
    const { error } = await supabase.from("user_roles").delete().eq("user_id", userId).eq("role", "admin");
    if (error) { toast.error(error.message); return; }
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-foreground">Team</h1>
      <Card className="p-4 text-sm text-muted-foreground space-y-2">
        <p><strong className="text-foreground">Inviting new admins:</strong> Have them sign up at <code>/auth</code>, then promote them below using their email.</p>
      </Card>
      <PromoteByEmail onDone={load} />
      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <SortableHead sortKey="full_name" sort={sort} onSort={(k) => setSort(s => toggleSort(s, k))}>Name</SortableHead>
            <SortableHead sortKey="email" sort={sort} onSort={(k) => setSort(s => toggleSort(s, k))}>Email</SortableHead>
            <SortableHead sortKey="role" sort={sort} onSort={(k) => setSort(s => toggleSort(s, k))}>Role</SortableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {sortedRows.map(r => (
              <TableRow key={`${r.user_id}-${r.role}`}>
                <TableCell>{r.full_name ?? "—"}</TableCell>
                <TableCell>{r.email ?? r.user_id}</TableCell>
                <TableCell><Badge>{r.role}</Badge></TableCell>
                <TableCell className="text-right">
                  {r.role === "admin" && r.user_id !== me && (
                    <Button size="sm" variant="outline" onClick={() => removeAdmin(r.user_id)}>Remove</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No team members yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function PromoteByEmail({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const promote = async () => {
    setLoading(true);
    try {
      const { data: profile, error: pErr } = await supabase.from("profiles").select("id").eq("email", email.trim()).maybeSingle();
      if (pErr) throw pErr;
      if (!profile) { toast.error("No user with that email — they must sign up first."); return; }
      const { error } = await supabase.from("user_roles").insert({ user_id: profile.id, role: "admin" });
      if (error) throw error;
      toast.success("Granted admin");
      setEmail("");
      onDone();
    } catch (e: any) {
      toast.error(e.message);
    } finally { setLoading(false); }
  };
  return (
    <Card className="p-4 flex gap-2 items-end">
      <div className="flex-1">
        <label className="text-xs text-muted-foreground">Promote user to admin (by email)</label>
        <input
          className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm mt-1"
          placeholder="user@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <Button onClick={promote} disabled={loading || !email}>Grant admin</Button>
    </Card>
  );
}
